import {
  ChangeDetectorRef,
  Component,
  OnDestroy,
  computed,
  inject,
  input,
  output,
  signal,
} from '@angular/core';

import { DatePipe, NgClass, NgTemplateOutlet } from '@angular/common';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';
import { dsEnums, webDtos } from '@vectronic/openApi-admin';
import { utcMoment } from '@vectronic/util';
import { Subscription } from 'rxjs';
import { AbstractScheduleService } from '../../services/abstract-schedule.service';
import { ScheduleCalendarCalculationService } from '../../services/schedule-calendar-calculation.service';
import { ScheduleServices } from '../../services/schedule.service';

@Component({
  selector: 'rc-calendar-year-view',
  templateUrl: './calendar-year-view.component.html',
  styleUrls: ['./calendar-year-view.component.scss'],
  imports: [MatIconButton, MatIcon, NgTemplateOutlet, NgClass, MatTooltip, DatePipe],
})
export class CalendarYearViewComponent implements OnDestroy {
  private cdr = inject(ChangeDetectorRef);
  private scheduleServices = inject(ScheduleServices);
  private calendarCalculationService = inject(ScheduleCalendarCalculationService);

  scheduleType = input<dsEnums.ScheduleType>();

  selectedDate = input<Date | null>();

  monthChange = output<Date>();
  yearChange = output<Date>();

  rulesArray = signal<webDtos.ScheduleRule[]>([]);
  months = signal<string[]>([
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ]);
  fixesOfLastYearMonths = signal<number[]>([]);
  fixesOfNextYearMonths = signal<number[]>([]);
  fixesOfMonths = signal<number[]>([]);
  averageOfMonths = signal<number[]>([]);
  averageOfLastYearMonths = signal<number[]>([]);
  averageOfNextYearMonths = signal<number[]>([]);

  subscription: Subscription;

  scheduleService = computed<AbstractScheduleService>(() => {
    if (!this.scheduleType()) return undefined;
    return this.scheduleServices[this.scheduleType()];
  });

  constructor() {
    toObservable(this.selectedDate)
      .pipe(takeUntilDestroyed())
      .subscribe(() => this.recompute());

    toObservable(this.scheduleService)
      .pipe(takeUntilDestroyed())
      .subscribe((service) => {
        this.subscription?.unsubscribe();
        if (service) {
          this.subscription = service.rules$.subscribe((rules) => {
            this.rulesArray.set(Object.values(rules));
            this.recompute();
          });
        }
      });
  }

  private recompute() {
    const selectedDate = this.selectedDate();
    if (!selectedDate) {
      this.fixesOfMonths.set([]);
      this.fixesOfLastYearMonths.set([]);
      this.fixesOfNextYearMonths.set([]);
      this.averageOfMonths.set([]);
      this.averageOfLastYearMonths.set([]);
      this.averageOfNextYearMonths.set([]);
      this.cdr.detectChanges();
      return;
    }

    const fixesOfMonths = this.months().map((_, index) => this.numberOfSchedulesInMonth(index));
    const fixesOfLastYearMonths = this.months().map((_, index) =>
      this.numberOfSchedulesInMonth(index, -1),
    );
    const fixesOfNextYearMonths = this.months().map((_, index) =>
      this.numberOfSchedulesInMonth(index, 1),
    );

    const averageOfMonths = this.months().map((_, index) => {
      const numberOfDaysInMonth = utcMoment(selectedDate, true).set('month', index).daysInMonth();
      return fixesOfMonths[index] / numberOfDaysInMonth || 0;
    });
    const averageOfLastYearMonths = this.months().map((_, index) => {
      const numberOfDaysInMonth = utcMoment(selectedDate, true).set('month', index).daysInMonth();
      return fixesOfLastYearMonths[index] / numberOfDaysInMonth || 0;
    });
    const averageOfNextYearMonths = this.months().map((_, index) => {
      const numberOfDaysInMonth = utcMoment(selectedDate, true).set('month', index).daysInMonth();
      return fixesOfNextYearMonths[index] / numberOfDaysInMonth || 0;
    });

    this.fixesOfMonths.set(fixesOfMonths);
    this.fixesOfLastYearMonths.set(fixesOfLastYearMonths);
    this.fixesOfNextYearMonths.set(fixesOfNextYearMonths);
    this.averageOfMonths.set(averageOfMonths);
    this.averageOfLastYearMonths.set(averageOfLastYearMonths);
    this.averageOfNextYearMonths.set(averageOfNextYearMonths);
    this.cdr.detectChanges();
  }

  ngOnDestroy() {
    this.subscription?.unsubscribe();
  }

  maxFix = computed(() => {
    const allAverages: number[] = [];

    const avg = this.averageOfMonths();
    if (avg && avg.length > 0) {
      allAverages.push(...avg);
    }

    const avgLast = this.averageOfLastYearMonths();
    if (avgLast && avgLast.length > 0) {
      allAverages.push(...avgLast);
    }

    const avgNext = this.averageOfNextYearMonths();
    if (avgNext && avgNext.length > 0) {
      allAverages.push(...avgNext);
    }

    if (allAverages.length === 0) return 0;
    return Math.max(...allAverages);
  });

  numberOfSchedulesInMonth(month: number, addYear = 0) {
    let number = 0;
    const service = this.scheduleService();
    if (service) {
      this.rulesArray()?.forEach((rule) => {
        number += this.calendarCalculationService.numberOfSchedulesInMonth(
          rule,
          utcMoment(this.selectedDate(), true).add({ year: addYear }).set('month', month),
          service.config,
        );
      });
    }
    return number;
  }

  onClickItem(index: number, isClickable: boolean) {
    if (isClickable)
      this.monthChange.emit(utcMoment(this.selectedDate(), true).set('month', index).toDate());
  }

  onClickPrevious() {
    this.yearChange.emit(utcMoment(this.selectedDate(), true).add('year', -1).toDate());
  }

  onClickNext() {
    this.yearChange.emit(utcMoment(this.selectedDate(), true).add('year', 1).toDate());
  }

  getPercent(average: number) {
    return Math.round((average * 100) / this.maxFix()) || 0;
  }
}
